<script setup>
import { ref, watch } from 'vue';
import * as calc from '../calc.js';
import { useShared } from '../state.js';
import { usePersisted } from '../use_persisted.js';
import { scopedKey, useSetups } from '../setups.js';
import { useToasts } from '../use_toasts.js';
import { useDeferredCompute } from '../use_deferred_compute.js';
import { yieldToFrame } from '../async_chunking.js';
import RecomputeButton from './recompute_button.vue';
import UnitSelect from './unit_select.vue';

const { inputs, computedResult, isStale, recompute, lookupLevelMark } = useShared();
const { pushToast } = useToasts();
const { activeSetupId } = useSetups();

// Scoped to the active setup — see mark.vue for why. Seeded with the
// example from the user's own setup: 0→inflection red and inflection→80m
// black both read off sight bar 1, then 70m→100m green off sight bar 12 —
// a real starting point rather than a placeholder nobody would use as-is.
const input = usePersisted(scopedKey('sight-tape-inputs', activeSetupId.value), {
  unit: 'm',
  width_mm: 4,
  zoom: 100,
  line_width_mm: 0.2,
  pointer_line_width_mm: 0.2,
  label_edge_padding_mm: 0.25,
  rail_gap_mm: 0.1,
  tick_side: 'left',
  background_color: '#ffffff',
  font_size_mm: 0.8,
  show_bar_indicator: true,
  show_unit_indicator: true,
  bar_indicator_position: 'bottom',
  ranges: [
    { start: 0, start_inflection: false, end: 0, end_inflection: true, color: '#c0392b', sight_bar_position: 1, tick_interval: 5 },
    { start: 0, start_inflection: true, end: 80, end_inflection: false, color: '#111111', sight_bar_position: 1, tick_interval: 5 },
    { start: 70, start_inflection: false, end: 100, end_inflection: false, color: '#2e7d32', sight_bar_position: 12, tick_interval: 5 },
  ],
}, {
  validate: (v) => Array.isArray(v?.ranges),
});

// Backfills fields added after some browsers already had a persisted
// sight-tape-inputs record — usePersisted loads whatever's stored as-is,
// so an older record simply won't have these keys yet.
if (input.value.line_width_mm === undefined) input.value.line_width_mm = 0.2;
// Carried over from line_width_mm (rather than a fresh default) — before
// this field existed, pointer/leader lines were drawn at line_width_mm
// too, so an existing record's own tape doesn't change width on upgrade.
if (input.value.pointer_line_width_mm === undefined) input.value.pointer_line_width_mm = input.value.line_width_mm;
if (input.value.label_edge_padding_mm === undefined) input.value.label_edge_padding_mm = 0.25;
if (input.value.rail_gap_mm === undefined) input.value.rail_gap_mm = 0.1;
if (input.value.tick_side === undefined) input.value.tick_side = 'left';
if (input.value.background_color === undefined) input.value.background_color = '#ffffff';
if (input.value.font_size_mm === undefined) input.value.font_size_mm = 0.8;
if (input.value.show_bar_indicator === undefined) input.value.show_bar_indicator = true;
if (input.value.show_unit_indicator === undefined) input.value.show_unit_indicator = true;
if (input.value.bar_indicator_position === undefined) input.value.bar_indicator_position = 'bottom';
// tick_interval used to be a single value shared by every range — carry an
// older record's own value over to each range individually rather than
// silently resetting everyone back to the default.
for (const range of input.value.ranges) {
  if (range.tick_interval === undefined) range.tick_interval = input.value.tick_interval ?? 5;
}

function addRange() {
  const ranges = input.value.ranges;
  const last = ranges[ranges.length - 1];
  ranges.push({
    start: last ? last.end : 0,
    start_inflection: false,
    end: last && Number.isFinite(last.end) ? last.end + 10 : 10,
    end_inflection: false,
    color: '#3e4c59',
    sight_bar_position: last ? last.sight_bar_position : (inputs.sight_settings.sight_bar_position ?? 1),
    tick_interval: last ? last.tick_interval : 5,
  });
}

function removeRange(index) {
  if (input.value.ranges.length > 1) input.value.ranges.splice(index, 1);
}

// ── Per-bar-position level-ground tables ──
//
// getLevelMarksTable() (state.js) only ever builds the table for whatever
// sight_bar_position is currently configured in Sight Settings — this panel
// needs one per DISTINCT bar position used across all ranges (bar 1 and bar
// 12 in the example above), so it calls calc.levelMarksTable directly and
// memoizes the result itself, the same fingerprint (settings|bar|calculated)
// state.js's own cache uses. Cleared wholesale past a generous cap rather
// than evicting individual entries — cheap to rebuild, and no session
// realistically cycles through more than a handful of distinct bar
// positions anyway.
const tableCache = new Map();
const TABLE_CACHE_LIMIT = 50;

function getTableForBar(barPosition) {
  const fingerprint = `${inputs.sight_settings}|${barPosition}|${computedResult.value}`;
  const cached = tableCache.get(fingerprint);
  if (cached) return cached;

  const rows = calc.levelMarksTable(inputs.sight_settings, barPosition, computedResult.value);
  if (tableCache.size >= TABLE_CACHE_LIMIT) tableCache.clear();
  tableCache.set(fingerprint, rows);
  return rows;
}

// The inflection point: mark-vs-distance isn't monotonic at short range (see
// rust/calc-core/src/physics/level_table.rs's own comment on this) — it
// decreases from a very large value near distance 0, bottoms out, then
// increases again for the rest of the table. That minimum is the split
// point between the two ranges a real tape needs its own colors for.
// Rounded to the nearest whole display-unit rather than left at the table's
// own fractional-meter precision — the split a shooter actually marks on a
// tape is a whole number of yards/meters, not something like 12.34.
function findInflectionDistance(rows, scale) {
  if (!rows || rows.length === 0) return null;
  let minIdx = 0;
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].mark < rows[minIdx].mark) minIdx = i;
  }
  return Math.round(rows[minIdx].distance / scale) * scale;
}

function inflectionDisplayFor(range) {
  if (isStale.value) return '—';
  try {
    const rows = getTableForBar(Number(range.sight_bar_position) || 1);
    const scale = calc.toScale(input.value.unit);
    const infl = findInflectionDistance(rows, scale);
    if (infl == null) return '—';
    return Math.round(infl / scale) + input.value.unit;
  } catch {
    return '—';
  }
}

// Inverts angle_to_mark's own physical-travel → mark conversion (see
// rust/calc-core/src/physics/mod.rs: `mark = physical_mm * MM_PER_M /
// mm_per_turn / sight_scale`, worked backwards from meters to mm) — the
// mm of housing travel along the threaded rod a given mark reading
// corresponds to. This is exactly the "sight physical range" quantity the
// image height is sized from.
function markToMm(mark) {
  const s = inputs.sight_settings;
  const mmPerTurn = 25.4 / s.vertical_tpi;
  return mark * s.sight_scale * mmPerTurn;
}

// ── Layout constants, all in physical mm ──
const VERTICAL_MARGIN_MM = 0.25;        // top/bottom padding beyond the sight's own full travel range
const LABEL_GAP_MM = 1.2;               // small gap between adjacent bits of text (e.g. bar-indicator entries)
// Minor (per-unit) ticks as a fraction of their OWN range's own major-tick
// length — shorter, so they read as in-between gradation rather than
// reference marks.
const MINOR_TO_MAJOR_TICK_RATIO = 0.5;
// Once the widest label (plus its own edge padding, and the rail gap on
// each side — see input.rail_gap_mm) has claimed its space, what's left of
// the tape's width is split between a pointer-line corridor and the mark
// zone — the corridor gets POINTER_CORRIDOR_FRACTION of whatever's left,
// floored at MIN_POINTER_LEADER_MM so it's never too narrow to actually
// route a leader line through, and the mark zone gets the rest, floored at
// MIN_MARK_LINE_LENGTH_MM so a mark line is never drawn too short to read.
// If the tape can't give both their own floor at once — see buildLayout's
// own "usableMm" check — there's nothing sensible left to draw, so nothing
// is: a warning instead of a squeezed, misleading tape.
const POINTER_CORRIDOR_FRACTION = 0.15;
const MIN_POINTER_LEADER_MM = 0.25;
const MIN_MARK_LINE_LENGTH_MM = 1;
// Each range's major-tick length steps down from the last, as a fraction of
// markZoneMm (range 0's own full length) — color alone isn't always enough
// to tell two groups apart at a glance, so length is a second, always-on
// cue, not just something that shows up when lines happen to be crowded
// together. Bounded to [RANGE_LENGTH_MIN_FRACTION, 1] rather than stepping
// down indefinitely — range 0 is always the full 100%, and however many
// ranges there are, none ever shrinks past RANGE_LENGTH_MIN_FRACTION.
const RANGE_LENGTH_STEP_FRACTION = 0.15;
const RANGE_LENGTH_MIN_FRACTION = 0.6;

const RANGE_ERROR_RETOAST_MS = 8000;
const lastRangeErrors = new Map();

function toastOnce(key, message) {
  const now = Date.now();
  const prev = lastRangeErrors.get(key);
  if (prev && prev.message === message && now - prev.at < RANGE_ERROR_RETOAST_MS) return;
  lastRangeErrors.set(key, { message, at: now });
  pushToast(message);
}

// Bare number, no unit suffix — the unit is shown once, top-center of the
// tape (see the unit indicator in renderTape), rather than repeated on
// every single label. Rounded to a whole display-unit — or to the range's
// own tick step, if that step is finer than a whole unit — rather than a
// fixed one-decimal-place format: a fixed decimal count still let unit-
// conversion float noise (e.g. yards→meters) print things like "5.0"
// instead of "5", and a 5-unit tick step has no business showing decimals
// at all.
function formatDistance(distanceM, unit, range) {
  const v = distanceM / calc.toScale(unit);
  const stepUnit = Math.max(0.01, Number(range?.tick_interval) || 1);
  const precision = Math.min(1, stepUnit);
  const rounded = Math.round(v / precision) * precision;
  return Number(rounded.toFixed(6)).toString();
}

// Measures how wide a string renders per 1px of font-size — a property of
// the font/string alone, independent of the actual size it's drawn at — so
// buildLayout can work out how much room a label needs in mm without
// caring what pxPerMm the eventual preview or export happens to use.
const measureCtx = document.createElement('canvas').getContext('2d');
const REF_FONT_PX = 200;

function textWidthRatio(text) {
  measureCtx.font = `${REF_FONT_PX}px sans-serif`;
  return measureCtx.measureText(text).width / REF_FONT_PX;
}

async function buildLayout() {
  if (isStale.value) await recompute();
  if (isStale.value) return null; // recompute() itself failed — see computeError

  const scale = calc.toScale(input.value.unit);

  // The tape's own height is the sight's FULL physical travel — 0 to
  // markToMm(100 / sight_scale) — not whatever range the configured
  // distances happen to produce: sight_scale is just which numbers are
  // printed on the dial (10 for a "0-10" face, 1 for "0-100"), and
  // mark·sight_scale is exactly 100 at the dial's own top end either way,
  // so this is always 100 mm-per-turn regardless of which face the sight
  // actually has fitted. Fixed bounds like this (rather than growing to
  // fit whatever ticks/labels show up) are what makes "drop anything
  // outside the hardware's own range" below meaningful — the ONLY thing
  // that can still change the tape's own height at that point is which
  // sight is fitted (vertical_tpi), never what distances were asked for.
  const mmPerTurn = 25.4 / inputs.sight_settings.vertical_tpi;
  const fullTravelMm = 100 * mmPerTurn;

  const majorTicks = [];
  const minorTicks = [];
  const minMm = 0;
  const maxMm = fullTravelMm;

  for (let i = 0; i < input.value.ranges.length; i++) {
    const range = input.value.ranges[i];
    const barPosition = Number(range.sight_bar_position) || 1;
    const stepUnit = Math.max(0.01, Number(range.tick_interval) || 1);

    let rows;
    try {
      rows = getTableForBar(barPosition);
    } catch (error) {
      toastOnce(`bar-${barPosition}`, `Sight Tape: couldn't build a mark table for sight bar ${barPosition} — ${error.message}`);
      continue;
    }

    const needsInflection = range.start_inflection || range.end_inflection;
    const infl = needsInflection ? findInflectionDistance(rows, scale) : null;
    if (needsInflection && infl == null) {
      toastOnce(`infl-${i}`, `Sight Tape: range ${i + 1} — couldn't find an inflection point for sight bar ${barPosition}`);
      continue;
    }

    const startM = range.start_inflection ? infl : Number(range.start) * scale;
    const endM = range.end_inflection ? infl : Number(range.end) * scale;
    if (!Number.isFinite(startM) || !Number.isFinite(endM)) continue;

    const loM = Math.min(startM, endM);
    const hiM = Math.max(startM, endM);
    const loUnit = loM / scale;
    const hiUnit = hiM / scale;

    // Major ticks: every tick_interval step within the range, plus both
    // endpoints — always labeled even when a boundary (e.g. an inflection
    // point) falls on a fractional distance the interval alone would skip.
    const majorDistancesM = new Set([loM, hiM]);
    for (let t = Math.ceil(loUnit / stepUnit) * stepUnit; t <= hiUnit + 1e-9; t += stepUnit) {
      majorDistancesM.add(t * scale);
    }

    // Minor ticks: one at every whole display-unit in between, skipping
    // anything that's already a major tick's own distance — unlabeled, and
    // shorter, so they read as in-between gradation rather than reference marks.
    const minorDistancesM = [];
    for (let t = Math.ceil(loUnit); t <= hiUnit + 1e-9; t += 1) {
      const dM = t * scale;
      const isMajor = Array.from(majorDistancesM).some(m => Math.abs(m - dM) < 1e-6);
      if (!isMajor) minorDistancesM.push(dM);
    }

    // A distance the user configured can still land outside the sight's
    // own physical travel (mark < 0 or > 100/sight_scale) — that's not a
    // tape-layout problem to work around, it's the sight literally not
    // being able to reach that far, so the tick is dropped even though it
    // was explicitly requested rather than stretching the tape to fit it.
    const EDGE_EPSILON_MM = 1e-6;
    let any = false;
    let anyOutOfRange = false;
    for (const distanceM of Array.from(majorDistancesM).sort((a, b) => a - b)) {
      const row = lookupLevelMark(rows, distanceM);
      if (!row) continue; // past wherever this bar's own table stopped being viable
      const mm = markToMm(row.mark);
      if (mm < -EDGE_EPSILON_MM || mm > fullTravelMm + EDGE_EPSILON_MM) { anyOutOfRange = true; continue; }
      majorTicks.push({ rangeIndex: i, range, distanceM, mm });
      any = true;
    }
    for (const distanceM of minorDistancesM) {
      const row = lookupLevelMark(rows, distanceM);
      if (!row) continue;
      const mm = markToMm(row.mark);
      if (mm < -EDGE_EPSILON_MM || mm > fullTravelMm + EDGE_EPSILON_MM) { anyOutOfRange = true; continue; }
      minorTicks.push({ rangeIndex: i, range, distanceM, mm });
    }

    if (!any) {
      toastOnce(`empty-${i}`, `Sight Tape: range ${i + 1} has no distances within the calculable range`);
    } else if (anyOutOfRange) {
      toastOnce(`out-of-range-${i}`, `Sight Tape: range ${i + 1} — some distances need a mark beyond the sight's own travel and were dropped`);
    }

    await yieldToFrame();
  }

  if (majorTicks.length === 0) return null;

  // Two ranges can share a boundary at the exact same PHYSICAL position
  // (most often an inflection point both a "before" and "after" range are
  // anchored to, on the same sight bar) — the lines are still both drawn
  // (they're genuinely different ranges' marks, still worth showing), but
  // printing the same distance twice right next to itself is just noise, so
  // only the first (by range order) gets a label. This has to key on `mm`,
  // not `distanceM`: two DIFFERENT ranges/bars can land on the same numeric
  // distance (e.g. two overlapping ranges both stepping through "70m") while
  // meaning entirely different physical positions — those must both keep
  // their label, only truly-identical positions should dedupe. majorTicks
  // is still in range-push order here (the mm-sort happens below), so
  // "first" here means lowest range index.
  {
    const seenPositions = new Set();
    for (const tick of majorTicks) {
      const key = tick.mm.toFixed(6);
      tick.skipLabel = seenPositions.has(key);
      seenPositions.add(key);
    }
  }

  const widthMm = Math.max(2, Number(input.value.width_mm) || 4);
  const labelEdgePaddingMm = Math.max(0, Number(input.value.label_edge_padding_mm) || 0.25);
  const railGapMm = Math.max(0, Number(input.value.rail_gap_mm) ?? 0.1);

  // Text size is the main control now — used directly, never auto-shrunk
  // to fit, and never silently capped either — so what you set is what
  // gets drawn, or nothing gets drawn at all (see the usableMm check
  // below). Everything else is worked out FROM it: the widest label (plus
  // its own edge padding) claims its space first, fixing labelLeftEdgeMm —
  // then a railGapMm-wide gap on EACH side (mark-tip-to-mark-rail, and
  // label-rail-to-label-edge — see markRailMm/labelRailMm below) is carved
  // out before what's left of THAT is split between the pointer-line
  // corridor and the mark zone (see POINTER_CORRIDOR_FRACTION's own
  // comment for how).
  let maxRatio = 0;
  for (const tick of majorTicks) {
    if (tick.skipLabel) continue;
    maxRatio = Math.max(maxRatio, textWidthRatio(formatDistance(tick.distanceM, input.value.unit, tick.range)));
  }
  const fontMm = Math.max(0.1, Number(input.value.font_size_mm) || 0.8);
  const maxLabelWidthMm = maxRatio * fontMm;
  const labelLeftEdgeMm = widthMm - maxLabelWidthMm - labelEdgePaddingMm;
  const usableMm = labelLeftEdgeMm - 2 * railGapMm;

  // Nothing sensible to draw if what's left (after both rail gaps) can't
  // give both their own floor — rather than let the corridor or the mark
  // zone quietly shrink past a size that's still meaningful (which is
  // exactly how PDF export used to lose its pointer lines with no
  // indication anything was wrong), refuse to draw at all and say why.
  if (usableMm < MIN_POINTER_LEADER_MM + MIN_MARK_LINE_LENGTH_MM) {
    toastOnce('too-narrow', `Sight Tape: text size (${fontMm.toFixed(2)}mm) leaves no room for both mark lines (min ${MIN_MARK_LINE_LENGTH_MM}mm) and pointer lines (min ${MIN_POINTER_LEADER_MM}mm), after the ${railGapMm.toFixed(2)}mm rail gaps, on a ${widthMm.toFixed(2)}mm-wide tape — reduce the text size, shrink the rail gap, or widen the tape.`);
    return null;
  }

  const pointerCorridorMm = Math.max(MIN_POINTER_LEADER_MM, usableMm * POINTER_CORRIDOR_FRACTION);
  const markZoneMm = usableMm - pointerCorridorMm;
  const markRailMm = markZoneMm + railGapMm;
  const labelRailMm = labelLeftEdgeMm - railGapMm;

  // Mark lines never move, and their length is fixed per range — stepped
  // down by range index so groups are distinguishable by length everywhere
  // on the tape, not just something that shows up where two happen to
  // collide. Color alone doesn't carry far enough on its own. Range 0 (the
  // longest) fills markZoneMm's own full length; later ranges step down
  // from that same basis (never below RANGE_LENGTH_MIN_FRACTION of it, so
  // they're always shorter than whatever's longest but never vanishingly
  // so) — never shorter than MIN_MARK_LINE_LENGTH_MM though, the same
  // floor markZoneMm itself was already guaranteed to clear above. Minor
  // ticks are a flat MINOR_TO_MAJOR_TICK_RATIO of their OWN range's own
  // (already-floored) major length, computed from that same major length
  // directly rather than re-deriving a separately-scaled zone, so the
  // ratio holds exactly whatever a range's own major length happens to be
  // — meaning a minor tick's own effective floor is
  // MIN_MARK_LINE_LENGTH_MM × MINOR_TO_MAJOR_TICK_RATIO, shorter than a
  // major tick's, which is the point: they're meant to read as shorter
  // in-between gradation, not full mark lines.
  function rangeBaseLength(rangeIndex, zoneMm) {
    const fraction = Math.max(RANGE_LENGTH_MIN_FRACTION, 1 - rangeIndex * RANGE_LENGTH_STEP_FRACTION);
    return Math.max(MIN_MARK_LINE_LENGTH_MM, zoneMm * fraction);
  }
  for (const tick of majorTicks) tick.lengthMm = rangeBaseLength(tick.rangeIndex, markZoneMm);
  for (const tick of minorTicks) tick.lengthMm = rangeBaseLength(tick.rangeIndex, markZoneMm) * MINOR_TO_MAJOR_TICK_RATIO;

  majorTicks.sort((a, b) => a.mm - b.mm);

  // Labels, unlike lines, ARE allowed to move a little to stay legible; the
  // line each one belongs to is still exactly where its own mark is, and
  // matching colors (see renderTape) is what keeps the pairing obvious even
  // when a label has drifted from its line.
  //
  // Solved as isotonic regression rather than by nudging colliding pairs
  // apart step by step: substituting z_i = mm_i - i·gapMm turns "every
  // label at least gapMm apart, in that order" into "z is non-decreasing",
  // and the classic pool-adjacent-violators algorithm finds the
  // non-decreasing fit to z with the least total (squared) displacement,
  // in one left-to-right pass — undoing the substitution (labelMm = fitted
  // z + i·gapMm) then gives back positions that are always exactly gapMm
  // apart, each moved the least amount necessary. Pooling is what
  // correctly handles chained collisions: a label that only touches one
  // edge of an already-crowded group gets merged into that SAME group and
  // the whole thing is re-centered together (an earlier, iterative "push
  // each colliding pair apart a little, repeat" version of this got stuck:
  // a label wedged between two simultaneously-colliding neighbors gets
  // pulled equally hard from both sides every step, so its net push is
  // exactly zero and interior gaps in any chain longer than 3 never
  // actually close — this has no such case and always terminates in one
  // pass, no iteration cap needed).
  const minLabelGapMm = fontMm * 1.2;
  const labelTicks = majorTicks.filter(t => !t.skipLabel);
  function fitLabelPositions(gapMm) {
    const blocks = []; // each: { sum, count, avg } of the z-values it pools
    for (let i = 0; i < labelTicks.length; i++) {
      const z = labelTicks[i].mm - i * gapMm;
      let block = { sum: z, count: 1, avg: z };
      while (blocks.length > 0 && blocks[blocks.length - 1].avg > block.avg) {
        const prev = blocks.pop();
        const sum = prev.sum + block.sum;
        const count = prev.count + block.count;
        block = { sum, count, avg: sum / count };
      }
      blocks.push(block);
    }
    const positions = [];
    let i = 0;
    for (const block of blocks) {
      for (let n = 0; n < block.count; n++) {
        positions[i] = block.avg + i * gapMm;
        i++;
      }
    }
    return positions;
  }

  // Labels must fit between the outermost marks — never allowed to push
  // the tape's own bounds out — so if the ideal (minLabelGapMm-spaced) fit
  // would need more room than [minMm, maxMm] actually has, the whole set
  // is refit with a smaller, EQUAL gap sized to exactly the room
  // available instead (a smaller gap can only ever narrow the isotonic
  // fit's own span, never widen it, so this refit is guaranteed to fit).
  // Members of a badly overcrowded run may still end up closer than
  // minLabelGapMm would ideally want — nothing fixes that without more
  // physical room — but they stay within the marks' own extent rather
  // than spilling past it.
  let positions = fitLabelPositions(minLabelGapMm);
  if (labelTicks.length > 1) {
    const naturalSpan = positions[positions.length - 1] - positions[0];
    const availableSpan = maxMm - minMm;
    if (naturalSpan > availableSpan) {
      positions = fitLabelPositions(availableSpan / (labelTicks.length - 1));
    }
  }
  // Translate the whole fit (preserving its own relative spacing) just
  // enough to bring it fully within [minMm, maxMm], in case minimizing
  // total displacement alone left an end sitting outside that range even
  // though the fit's own span fits.
  if (labelTicks.length > 0) {
    const lowerShiftMm = minMm - positions[0];
    const upperShiftMm = maxMm - positions[positions.length - 1];
    const shiftMm = Math.min(Math.max(0, lowerShiftMm), upperShiftMm);
    for (let i = 0; i < positions.length; i++) labelTicks[i].labelMm = positions[i] + shiftMm;
  }

  // Bar-position indicator: every range's sight bar, each in its own color,
  // so it's always visible which bar a colored group of marks assumes —
  // not just the ones that differ from what Sight Settings has configured.
  const seenBarEntries = new Set();
  const barEntries = [];
  for (const range of input.value.ranges) {
    const bar = Number(range.sight_bar_position) || 1;
    const key = `${bar}|${range.color}`;
    if (seenBarEntries.has(key)) continue;
    seenBarEntries.add(key);
    barEntries.push({ text: String(bar), color: range.color });
  }
  const showBarIndicator = input.value.show_bar_indicator !== false && barEntries.length > 0;
  const showUnitIndicator = input.value.show_unit_indicator !== false;
  const barRowHeightMm = fontMm * 1.8;

  // Unlike tick labels (never auto-shrunk — see fontMm's own comment), this
  // row has no reserved space and the tape itself can be just a few mm
  // wide, so at fontMm's own size a handful of bar entries would routinely
  // run off both edges. Squash the font (and gap) down just enough to fit
  // the full row within widthMm, keeping every entry's own relative size —
  // never grown past fontMm, since a short row shouldn't stretch to fill
  // the width.
  let barFontMm = fontMm;
  if (showBarIndicator) {
    const rawWidthsMm = barEntries.map(e => textWidthRatio(e.text) * fontMm);
    const rawTotalMm = rawWidthsMm.reduce((a, b) => a + b, 0) + LABEL_GAP_MM * (barEntries.length - 1);
    if (rawTotalMm > widthMm) barFontMm = fontMm * (widthMm / rawTotalMm);
  }
  const barGapMm = LABEL_GAP_MM * (barFontMm / fontMm);
  const barWidthsMm = barEntries.map(e => textWidthRatio(e.text) * barFontMm);
  const barTotalWidthMm = barWidthsMm.reduce((a, b) => a + b, 0) + barGapMm * Math.max(0, barEntries.length - 1);
  let barX = (widthMm - barTotalWidthMm) / 2;
  for (let i = 0; i < barEntries.length; i++) {
    barEntries[i].xMm = barX;
    barEntries[i].widthMm = barWidthsMm[i];
    barX += barWidthsMm[i] + barGapMm;
  }
  const barPosition = input.value.bar_indicator_position === 'top' ? 'top' : 'bottom';
  // The unit and bar-position indicators don't get their own reserved
  // space any more — the tape's own height is the sight's fixed physical
  // travel (see fullTravelMm above), not something that grows to make room
  // for extra chrome. Both are instead drawn — deliberately LAST, so they
  // paint over anything already there — right at the tape's own top/bottom
  // edge, where a real mark is least likely to land anyway; overlapping
  // whatever tick/label happens to be there is an accepted tradeoff, and
  // is why there's now a way to hide either one.
  const unitRowHeightMm = fontMm * 1.8;

  const layout = {
    majorTicks,
    minorTicks,
    labelTicks,
    minMm,
    maxMm,
    unit: input.value.unit,
    widthMm,
    markRailMm,
    labelRailMm,
    labelEdgePaddingMm,
    fontMm,
    unitRowHeightMm,
    heightMm: VERTICAL_MARGIN_MM * 2 + (maxMm - minMm),
    side: input.value.tick_side === 'right' ? 'right' : 'left',
    lineWidthMm: Math.max(0.05, Number(input.value.line_width_mm) || 0.2),
    pointerLineWidthMm: Math.max(0.05, Number(input.value.pointer_line_width_mm) || 0.2),
    backgroundColor: input.value.background_color || '#ffffff',
    barEntries,
    showBarIndicator,
    showUnitIndicator,
    barRowHeightMm,
    barFontMm,
    barPosition,
  };
  // Computed once, here, in pure mm-space — see buildDrawOps' own comment
  // for why both renderTape and buildPdfContentStream just walk this same
  // list rather than each re-deriving the geometry themselves.
  layout.ops = buildDrawOps(layout);
  return layout;
}

// Far distances at the bottom: larger mark/physical position sinks toward
// the bottom of the printed strip.
function yForMm(mm, layout) {
  return VERTICAL_MARGIN_MM + (mm - layout.minMm);
}

// A flat list of draw commands ("ops"), computed once in pure mm-space from
// a finished layout — every geometry decision (mirroring rails/positions
// for tick_side, clamping pointer/leader lines to their own rails, label
// alignment) happens exactly once, here. renderTape (canvas, below) and
// buildPdfContentStream (PDF, further down) are then both just dumb
// backends: they walk this same list and convert mm to their own unit (px
// or pt) — no geometry logic of their own left to duplicate, and so
// nothing left that can let the two drift apart (this used to be two
// independent implementations, and the PDF one silently dropped every
// pointer line on a right-side tick layout because its own copy of the
// rail math forgot to mirror them the way renderTape's did).
function buildDrawOps(layout) {
  const isLeft = layout.side === 'left';
  const ops = [];

  ops.push({ kind: 'rect', xMm: 0, yMm: 0, wMm: layout.widthMm, hMm: layout.heightMm, color: layout.backgroundColor });

  // All lines, longest first — a shorter, later-drawn line never fully
  // hides a longer one it's staggered against, since the longer line's own
  // tail still extends past where the shorter one ends. Major ticks whose
  // label was suppressed as a duplicate (see buildLayout's own dedup pass)
  // sit at the EXACT same position as another range's own tick — drawing a
  // second, shorter line right on top of the first would only bury most of
  // that first line's length, leaving a sliver too thin to look like it's
  // really there, for a line that adds no new physical position anyway.
  const allLines = [...layout.majorTicks.filter(t => !t.skipLabel), ...layout.minorTicks].sort((a, b) => b.lengthMm - a.lengthMm);
  for (const tick of allLines) {
    const mm = yForMm(tick.mm, layout);
    const fromMm = isLeft ? 0 : layout.widthMm - tick.lengthMm;
    const toMm = fromMm + tick.lengthMm;
    ops.push({ kind: 'line', x1Mm: fromMm, y1Mm: mm, x2Mm: toMm, y2Mm: mm, color: tick.range.color, widthMm: layout.lineWidthMm });
  }

  // Labels — aligned against the tape's own outer edge (opposite the tick
  // zone) with a small consistent padding, rather than ragged against the
  // tick zone. Colored to match its own line; when a label has actually
  // been nudged away from its line's true position, a straight leader
  // (same color) connects the true end of the mark line to a point just
  // short of the label — level with the label's own vertical center, but
  // stopping short of the text rather than running through it.
  const labelEdgePaddingMm = layout.labelEdgePaddingMm;
  const labelXMm = isLeft ? layout.widthMm - labelEdgePaddingMm : labelEdgePaddingMm;
  const MIN_LEADER_MM = 0.01; // below this, the label sits close enough to its line that a connector is just noise

  // Two invisible reference lines ("rails"), both already derived in
  // buildLayout FROM the actual rendered label extent (see markRailMm's own
  // comment there) — a mark-rail clear of the longest mark line on the
  // whole tape, and a label-rail clear of the widest label on the whole
  // tape. Global, not per-range: a mark-rail based on only THIS tick's own
  // range would sit closer to the edge than a longer range's actual line,
  // and a pointer walking out to it could then cross straight through that
  // other, longer range's mark line wherever the two happen to be near the
  // same height (ranges can and do overlap the same distances, e.g. two
  // ranges both covering 0–10m) — the shared rail is what guarantees a
  // pointer never crosses ANY range's line, not just its own. Stored on
  // layout as "mm in from the tick side" — mirrored onto the tape's actual
  // physical edge right here, the one place that mirroring needs to happen.
  const markRailMm = isLeft ? layout.markRailMm : layout.widthMm - layout.markRailMm;
  const labelRailMm = isLeft ? layout.labelRailMm : layout.widthMm - layout.labelRailMm;

  for (const tick of layout.labelTicks) {
    const tickMm = yForMm(tick.mm, layout);
    const labelMm = yForMm(tick.labelMm, layout);
    const text = formatDistance(tick.distanceM, layout.unit, tick.range);
    const textWidthMm = textWidthRatio(text) * layout.fontMm;
    const textLeftMm = isLeft ? labelXMm - textWidthMm : labelXMm;

    if (Math.abs(tick.labelMm - tick.mm) > MIN_LEADER_MM) {
      // The two TRUE points this pointer is aimed between — where this
      // tick's own mark line actually ends, and this label's own near edge
      // (the side facing the tick zone) at its vertical center.
      const markEndMm = isLeft ? tick.lengthMm : layout.widthMm - tick.lengthMm;
      const targetMm = isLeft ? labelXMm - textWidthMm : labelXMm + textWidthMm;
      const dxMm = targetMm - markEndMm;
      const dyMm = labelMm - tickMm;
      const safeDx = dxMm || (isLeft ? 1e-6 : -1e-6);

      // Walk that same line out to where it crosses each rail, rather than
      // drawing between the true points directly — then clamp to [0,1] so
      // it never walks PAST either true point to get there. A rail can
      // land outside this tick's own [mark end, label edge] segment when a
      // shorter range's own line ends well before the rail, or a wide
      // label leaves little room — clamping means a rail is only ever used
      // when it actually falls between the two true points; otherwise the
      // line simply touches the true endpoint directly instead of
      // overshooting past it.
      const tStart = Math.min(Math.max((markRailMm - markEndMm) / safeDx, 0), 1);
      const tEnd = Math.min(Math.max((labelRailMm - markEndMm) / safeDx, 0), 1);

      if (tEnd > tStart) {
        ops.push({
          kind: 'line',
          x1Mm: markEndMm + tStart * dxMm, y1Mm: tickMm + tStart * dyMm,
          x2Mm: markEndMm + tEnd * dxMm, y2Mm: tickMm + tEnd * dyMm,
          color: tick.range.color, widthMm: layout.pointerLineWidthMm,
        });
      }
    }

    ops.push({ kind: 'text', xMm: textLeftMm, yMm: labelMm, text, color: tick.range.color, fontMm: layout.fontMm });
  }

  // Unit indicator: shown once, top-center, rather than repeated on every
  // label (see formatDistance's own comment) — drawn last (after every
  // mark/pointer/label above) and right at the tape's own top edge rather
  // than in space reserved for it, so it can paint over whatever's there.
  if (layout.showUnitIndicator) {
    const unitWidthMm = textWidthRatio(layout.unit) * layout.fontMm;
    ops.push({ kind: 'text', xMm: layout.widthMm / 2 - unitWidthMm / 2, yMm: layout.unitRowHeightMm / 2, text: layout.unit, color: '#000000', fontMm: layout.fontMm });
  }

  // Bar-position indicator: every range's sight bar, each in its own color
  // — likewise drawn last, right at the tape's own top/bottom edge.
  if (layout.showBarIndicator) {
    const unitRowHeightMm = layout.showUnitIndicator ? layout.unitRowHeightMm : 0;
    const rowTopMm = layout.barPosition === 'top' ? unitRowHeightMm : layout.heightMm - layout.barRowHeightMm;
    const rowCenterMm = rowTopMm + layout.barRowHeightMm / 2;
    for (const entry of layout.barEntries) {
      ops.push({ kind: 'text', xMm: entry.xMm, yMm: rowCenterMm, text: entry.text, color: entry.color, fontMm: layout.barFontMm });
    }
  }

  return ops;
}

// The live on-screen preview — a thin backend over buildDrawOps' shared
// mm-space op list, converting every coordinate to px. No geometry
// decisions belong here; see buildDrawOps' own comment.
function renderTape(ctx, layout, pxPerMm) {
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';

  for (const op of layout.ops) {
    if (op.kind === 'rect') {
      ctx.fillStyle = op.color;
      ctx.fillRect(op.xMm * pxPerMm, op.yMm * pxPerMm, op.wMm * pxPerMm, op.hMm * pxPerMm);
    } else if (op.kind === 'line') {
      ctx.strokeStyle = op.color;
      ctx.lineWidth = Math.max(0.5, op.widthMm * pxPerMm);
      ctx.beginPath();
      ctx.moveTo(op.x1Mm * pxPerMm, op.y1Mm * pxPerMm);
      ctx.lineTo(op.x2Mm * pxPerMm, op.y2Mm * pxPerMm);
      ctx.stroke();
    } else if (op.kind === 'text') {
      ctx.font = `${Math.max(6, op.fontMm * pxPerMm)}px sans-serif`;
      ctx.fillStyle = op.color;
      ctx.fillText(op.text, op.xMm * pxPerMm, op.yMm * pxPerMm);
    }
  }
}

const myCanvas = ref(null);
let currentLayout = null;

async function refreshPreview() {
  const layout = await buildLayout();
  currentLayout = layout;
  if (!myCanvas.value) return;

  if (!layout) {
    myCanvas.value.width = 0;
    myCanvas.value.height = 0;
    return;
  }

  // Zoom is on-screen convenience only (a narrow strip is unreadable at
  // true CSS size) — PDF export (see downloadPdf) always renders at the
  // tape's true physical mm dimensions regardless of this.
  const zoomPct = Math.min(2000, Math.max(25, Number(input.value.zoom) || 100));
  const previewPxPerMm = (zoomPct / 100) * (96 / 25.4);
  const dpr = window.devicePixelRatio || 1;

  const widthPx = layout.widthMm * previewPxPerMm;
  const heightPx = layout.heightMm * previewPxPerMm;

  myCanvas.value.width = Math.round(widthPx * dpr);
  myCanvas.value.height = Math.round(heightPx * dpr);
  myCanvas.value.style.width = widthPx + 'px';
  myCanvas.value.style.height = heightPx + 'px';

  const ctx = myCanvas.value.getContext('2d');
  ctx.scale(dpr, dpr);

  renderTape(ctx, layout, previewPxPerMm);
}

// PDF export instead of a rasterized PNG — a PDF page's size is physical
// geometry baked into the document itself (in points, 1/72in), not optional
// metadata a viewer can choose to ignore the way a PNG's DPI tag can be (see
// git history for that attempt) — Preview's "Actual Size" and virtually any
// print pipeline treat it as authoritative, so there's no DPI-guessing for
// a viewer to get wrong. Built by hand rather than pulling in a PDF library:
// a single-page document with a handful of line/text drawing operators is a
// tiny, well-defined subset of the format, and buildPdfContentStream (below)
// walks the exact same buildDrawOps list renderTape does, just emitting PDF
// operators in physical mm→pt instead of canvas calls in px — see
// buildDrawOps' own comment for why the geometry is guaranteed to match. No
// library is needed to DISPLAY it either — opening/printing a PDF is a
// capability every OS and browser already has; we only ever generate the
// bytes.
const PT_PER_MM = 72 / 25.4;

// Cut-line guide: a light-grey band drawn entirely outside the tape's own
// bounds, in a margin added just for it — never overlapping or resizing the
// tape content itself (which stays at its exact physical size, see
// VERTICAL_MARGIN_MM's own comment). The margin is sized to exactly match
// the border's width, so the band fills it edge-to-edge with no gap.
const CUT_BORDER_WIDTH_MM = 2;
const CUT_BORDER_COLOR = [0.8, 0.8, 0.8];

// Wraps a tape's own content stream (in the tape's local coordinate frame,
// origin at its bottom-left) inside a larger page that adds CUT_BORDER_WIDTH_MM
// of margin on every side, translates the tape content into that page
// unchanged, and stamps the border band into the margin itself.
function buildBorderedPdfBytes(layout, tapeContentStream) {
  const marginMm = CUT_BORDER_WIDTH_MM;
  const pageWidthPt = (layout.widthMm + marginMm * 2) * PT_PER_MM;
  const pageHeightPt = (layout.heightMm + marginMm * 2) * PT_PER_MM;
  const marginPt = marginMm * PT_PER_MM;

  const borderInsetPt = (CUT_BORDER_WIDTH_MM / 2) * PT_PER_MM;
  const [cbR, cbG, cbB] = CUT_BORDER_COLOR;
  const borderOps = [
    `${(CUT_BORDER_WIDTH_MM * PT_PER_MM).toFixed(2)} w`,
    `${cbR.toFixed(3)} ${cbG.toFixed(3)} ${cbB.toFixed(3)} RG`,
    `${borderInsetPt.toFixed(2)} ${borderInsetPt.toFixed(2)} ${(pageWidthPt - CUT_BORDER_WIDTH_MM * PT_PER_MM).toFixed(2)} ${(pageHeightPt - CUT_BORDER_WIDTH_MM * PT_PER_MM).toFixed(2)} re`,
    'S',
  ].join('\n');

  const contentStream = `q\n1 0 0 1 ${marginPt.toFixed(2)} ${marginPt.toFixed(2)} cm\n${tapeContentStream}Q\n${borderOps}\n`;
  return buildPdfBytes(pageWidthPt, pageHeightPt, contentStream);
}

function hexToRgb01(hex) {
  const clean = hex.replace('#', '');
  return [0, 2, 4].map(i => parseInt(clean.substring(i, i + 2), 16) / 255);
}

function pdfEscapeText(str) {
  return str.replace(/([()\\])/g, '\\$1');
}

// A thin backend over buildDrawOps' shared mm-space op list, converting
// every coordinate to pt instead of px. No geometry decisions belong here;
// see buildDrawOps' own comment.
function buildPdfContentStream(layout) {
  const heightPt = layout.heightMm * PT_PER_MM;
  const ops = [];

  const xPt = (xMm) => (xMm * PT_PER_MM).toFixed(2);
  const yPt = (yMmFromTop) => (heightPt - yMmFromTop * PT_PER_MM).toFixed(2);

  for (const op of layout.ops) {
    if (op.kind === 'rect') {
      const [r, g, b] = hexToRgb01(op.color);
      ops.push(`${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} rg`);
      ops.push(`${xPt(op.xMm)} ${yPt(op.yMm + op.hMm)} ${(op.wMm * PT_PER_MM).toFixed(2)} ${(op.hMm * PT_PER_MM).toFixed(2)} re f`);
    } else if (op.kind === 'line') {
      const [r, g, b] = hexToRgb01(op.color);
      ops.push(`${(op.widthMm * PT_PER_MM).toFixed(2)} w`, `${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} RG`);
      ops.push(`${xPt(op.x1Mm)} ${yPt(op.y1Mm)} m`, `${xPt(op.x2Mm)} ${yPt(op.y2Mm)} l`, 'S');
    } else if (op.kind === 'text') {
      // PDF text is positioned by its baseline, not a vertical center the
      // way canvas's textBaseline='middle' is — 0.32× the font size is a
      // standard approximation for how far a typical sans-serif's baseline
      // sits below its own vertical center.
      const [r, g, b] = hexToRgb01(op.color);
      const fontPt = op.fontMm * PT_PER_MM;
      const baselineOffsetPt = fontPt * 0.32;
      ops.push('BT', `/F1 ${fontPt.toFixed(2)} Tf`, `${r.toFixed(3)} ${g.toFixed(3)} ${b.toFixed(3)} rg`);
      ops.push(`${xPt(op.xMm)} ${(heightPt - op.yMm * PT_PER_MM - baselineOffsetPt).toFixed(2)} Td`);
      ops.push(`(${pdfEscapeText(op.text)}) Tj`, 'ET');
    }
  }

  return ops.join('\n') + '\n';
}

// Assembles a minimal single-page PDF: Catalog → Pages → Page → content
// stream → the one font resource (Helvetica, one of the 14 standard PDF
// fonts every viewer already has built in, so nothing to embed). xref
// offsets are byte positions into THIS file, so objects are written in
// order while tracking how many bytes have gone out so far.
function buildPdfBytes(widthPt, heightPt, contentStream) {
  const enc = new TextEncoder();
  const parts = [];
  let offset = 0;
  const objectOffsets = {};
  function pushBytes(bytes) { parts.push(bytes); offset += bytes.length; }
  function pushStr(str) { pushBytes(enc.encode(str)); }

  pushStr('%PDF-1.4\n');

  objectOffsets[1] = offset;
  pushStr('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n');

  objectOffsets[2] = offset;
  pushStr('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n');

  objectOffsets[3] = offset;
  pushStr(`3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${widthPt.toFixed(2)} ${heightPt.toFixed(2)}] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >>\nendobj\n`);

  objectOffsets[4] = offset;
  const streamBytes = enc.encode(contentStream);
  pushStr(`4 0 obj\n<< /Length ${streamBytes.length} >>\nstream\n`);
  pushBytes(streamBytes);
  pushStr('\nendstream\nendobj\n');

  objectOffsets[5] = offset;
  pushStr('5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n');

  const xrefOffset = offset;
  let xref = 'xref\n0 6\n0000000000 65535 f \n';
  for (let i = 1; i <= 5; i++) xref += String(objectOffsets[i]).padStart(10, '0') + ' 00000 n \n';
  pushStr(xref);
  pushStr(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  const result = new Uint8Array(offset);
  let pos = 0;
  for (const part of parts) { result.set(part, pos); pos += part.length; }
  return result;
}

async function downloadPdf() {
  const layout = currentLayout ?? await buildLayout();
  if (!layout) {
    pushToast('Sight Tape: nothing to export — add at least one valid range first.');
    return;
  }

  const pdfBytes = buildBorderedPdfBytes(layout, buildPdfContentStream(layout));

  const url = URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'sight-tape.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

// The initial draw is deferred (see use_deferred_compute.js) so the panel's
// own open animation gets to paint and start before this begins — a later
// redraw (the watch below) isn't deferred since the panel is already open
// by then, with no animation racing it.
const pending = useDeferredCompute(() => refreshPreview());

watch([computedResult, input], () => {
  refreshPreview();
}, { deep: true });
</script>

<template>
  <div class="centered">
    <RecomputeButton />

    <div class="controls">
      <label>Unit:
        <UnitSelect v-model="input.unit" />
      </label>

      <label>Tape width:
        <input type="number" class="input-measure" v-model.number="input.width_mm" min="1" step="0.5" /> mm
      </label>

      <label>Text size:
        <input type="number" class="input-measure" v-model.number="input.font_size_mm" min="0.5" step="0.5" /> mm
      </label>

      <label>Line width:
        <input type="number" class="input-measure" v-model.number="input.line_width_mm" min="0.05" step="0.05" /> mm
      </label>

      <label>Pointer line width:
        <input type="number" class="input-measure" v-model.number="input.pointer_line_width_mm" min="0.05" step="0.05" /> mm
      </label>

      <label>Label edge padding:
        <input type="number" class="input-measure" v-model.number="input.label_edge_padding_mm" min="0" step="0.05" /> mm
      </label>

      <label>Rail gap:
        <input type="number" class="input-measure" v-model.number="input.rail_gap_mm" min="0" step="0.05" title="Gap between a mark line's own tip and the pointer-line rail, and between the pointer-line rail and a label's own edge" /> mm
      </label>

      <label>Background:
        <input type="color" class="input-color" v-model="input.background_color" />
      </label>

      <label>Line side:
        <div class="side-select" role="group" aria-label="Line side">
          <button type="button" class="side-option"
            :class="{ active: input.tick_side !== 'right' }"
            @click="input.tick_side = 'left'">
            -- 10m
          </button>
          <button type="button" class="side-option"
            :class="{ active: input.tick_side === 'right' }"
            @click="input.tick_side = 'right'">
            10m --
          </button>
        </div>
      </label>

      <label>Zoom:
        <input type="number" class="input-measure" v-model.number="input.zoom" min="25" max="2000" step="25" /> %
      </label>

      <button @click="downloadPdf" class="btn-copy">
        Download PDF
      </button>
    </div>

    <div class="controls">
      <label class="checkbox-label">
        <input type="checkbox" v-model="input.show_unit_indicator" />
        Show unit
      </label>

      <label class="checkbox-label">
        <input type="checkbox" v-model="input.show_bar_indicator" />
        Show off-bar indicator
      </label>

      <label>Position:
        <div class="side-select" role="group" aria-label="Bar indicator position">
          <button type="button" class="side-option"
            :class="{ active: input.bar_indicator_position !== 'bottom' }"
            @click="input.bar_indicator_position = 'top'">
            Top
          </button>
          <button type="button" class="side-option"
            :class="{ active: input.bar_indicator_position === 'bottom' }"
            @click="input.bar_indicator_position = 'bottom'">
            Bottom
          </button>
        </div>
      </label>
    </div>

    <div class="table-scroll-wrapper">
      <table>
        <thead>
          <tr>
            <th>Start</th>
            <th>End</th>
            <th>Color</th>
            <th>Sight Bar</th>
            <th>Tick Interval</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(range, index) in input.ranges" :key="index">
            <td>
              <div class="boundary-cell">
                <input type="number"
                  class="input-measure input-boundary"
                  v-model.number="range.start"
                  :disabled="range.start_inflection" />
                <button type="button" class="btn-toggle-infl"
                  :class="{ active: range.start_inflection }"
                  @click="range.start_inflection = !range.start_inflection"
                  title="Use this range's inflection point instead of a fixed distance">
                  Infl.
                </button>
              </div>
              <span v-if="range.start_inflection" class="infl-hint">{{ inflectionDisplayFor(range) }}</span>
            </td>
            <td>
              <div class="boundary-cell">
                <input type="number"
                  class="input-measure input-boundary"
                  v-model.number="range.end"
                  :disabled="range.end_inflection" />
                <button type="button" class="btn-toggle-infl"
                  :class="{ active: range.end_inflection }"
                  @click="range.end_inflection = !range.end_inflection"
                  title="Use this range's inflection point instead of a fixed distance">
                  Infl.
                </button>
              </div>
              <span v-if="range.end_inflection" class="infl-hint">{{ inflectionDisplayFor(range) }}</span>
            </td>
            <td>
              <input type="color" class="input-color" v-model="range.color" title="Range color" />
            </td>
            <td>
              <input type="number" class="input-measure input-sight-bar" v-model.number="range.sight_bar_position" />
            </td>
            <td>
              <input type="number" class="input-measure input-tick-interval" v-model.number="range.tick_interval" min="0.1" step="1" />
              {{ input.unit }}
            </td>
            <td>
              <button
                @click="removeRange(index)"
                class="btn-delete"
                title="Remove range"
                aria-label="Remove range"
                :disabled="input.ranges.length <= 1"
              >
                ✕
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button @click="addRange" class="btn-add">
      + Add Range
    </button>
  </div>

  <div class="tape-wrap">
    <p v-if="pending" class="computing-placeholder">Computing…</p>
    <div class="tape-scroll-wrapper">
      <canvas ref="myCanvas"></canvas>
    </div>
  </div>
</template>

<style scoped>

.controls {
  display: flex;
  gap: var(--space-lg);
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: var(--space-sm);
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-xs);
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
}

th, td {
  padding: 0.25rem 0.4rem;
  vertical-align: top;
  text-align: center;
}

.boundary-cell {
  display: flex;
  align-items: center;
  gap: 2px;
}

.input-boundary {
  max-width: calc(6ch + 10px);
}

.input-sight-bar {
  max-width: calc(6ch + 10px);
}

.input-tick-interval {
  max-width: calc(5ch + 10px);
}

.btn-toggle-infl {
  padding: var(--space-xs) 0.4rem;
  border: var(--border-width) solid var(--input-border);
  border-radius: var(--border-radius);
  background: var(--input-bg);
  color: var(--input-text);
  font-size: var(--font-size-sm);
  cursor: pointer;
  white-space: nowrap;
}

.btn-toggle-infl.active {
  background: var(--color-accent);
  color: var(--color-text-light);
  border-color: var(--color-accent);
}

.infl-hint {
  display: block;
  font-size: 0.7rem;
  color: var(--color-text);
  opacity: 0.6;
  white-space: nowrap;
}

.input-color {
  width: 2.4rem;
  height: 1.8rem;
  padding: 0;
  border: var(--border-width) solid var(--input-border);
  border-radius: var(--border-radius);
  background: none;
  cursor: pointer;
}

.side-select {
  display: inline-flex;
  border: var(--border-width) solid var(--input-border);
  border-radius: var(--border-radius);
  overflow: hidden;
  vertical-align: middle;
}

.side-option {
  padding: var(--space-xs) var(--space-sm);
  border: none;
  background: var(--input-bg);
  color: var(--input-text);
  cursor: pointer;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
}

.side-option + .side-option {
  border-left: var(--border-width) solid var(--input-border);
}

.side-option.active {
  background: var(--color-accent);
  color: var(--color-text-light);
}

.tape-wrap {
  position: relative;
  margin-top: var(--space-sm);
}

.tape-scroll-wrapper {
  overflow: auto;
  max-height: 80vh;
  display: flex;
  justify-content: center;
  border: var(--border);
  padding: var(--space-sm);
}

.computing-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  opacity: 0.7;
}

</style>
